import { defineStore } from "pinia";
import { privateBoaHttp } from "@/boa-api-client";
import type { PurchasedProduct, Ticket } from "best-of-awards-api";
import { HTTPError } from "ky";
import { validNumber } from "@/util/phone";
import { validEmail } from "@/util/email";
import useCandidatesStore from "@/stores/candidates";
import useAwardStore from "@/stores/award";
import useProductsStore from "@/stores/products";
import { useAuthStore } from ".";

export type CandidateTickets = {
  candidateName: string;
  candidatePK: string;
  tickets: Array<Ticket>;
};

type State = {
  candidateTickets: Array<CandidateTickets>;
};

export const validTicketHolder = (t: Ticket) => {
  return (
    /\w+/.test(t.holder.firstName) &&
    /\w+/.test(t.holder.lastName) &&
    (validEmail(t.holder.email) || validNumber(t.holder.mobile))
  );
};

export default defineStore("tickets", {
  state: (): State => ({
    candidateTickets: [],
  }),
  getters: {
    uniqueHolderTickets: (state) => {
      return state.candidateTickets.reduce<Array<Ticket>>((acc, candidate) => {
        const uniques: Array<Ticket> = [];
        const sortedTickets = [...candidate.tickets].sort(
          (a, b) => a.ticketNumber - b.ticketNumber
        );
        for (const ticket of sortedTickets) {
          if (!ticket.holder.email && !ticket.holder.mobile) {
            continue;
          }
          if (
            !uniques.some(
              (unique) =>
                unique.holder.email &&
                ticket.holder.email.toLocaleLowerCase().replace(/\s+/g, "") ===
                  unique.holder.email.toLocaleLowerCase().replace(/\s+/g, "") &&
                unique.holder.mobile &&
                unique.holder.mobile.toLocaleLowerCase().replace(/\s+/g, "") ===
                  ticket.holder.mobile.toLocaleLowerCase().replace(/\s+/g, "")
            )
          ) {
            uniques.push(ticket);
          }
        }
        acc = acc.concat(uniques);
        return acc;
      }, []);
    },
    upgradeableTicketCount: (state): number => {
      const awardStore = useAwardStore();
      if (awardStore.awardsCeremonyOver) {
        return 0;
      }
      const productsStore = useProductsStore();
      const candidatesStore = useCandidatesStore();
      const currentCandidate = candidatesStore.currentCandidate;
      if (!currentCandidate) {
        return 0;
      }
      const vipTicket = productsStore.ceremonyTicket?.options.find(
        (option) =>
          option.type === "VIPCeremonyTicket" &&
          option.awardYear === awardStore.awardYear
      );
      const availableVip =
        vipTicket?.nights[candidatesStore.currentCandidateCeremonyNight]
          ?.quantityAvailable ?? 0;
      const candidateTickets = state.candidateTickets.find(
        (candidate) =>
          candidate.candidatePK === `CANDIDATE#${currentCandidate.id}`
      );
      const generalTickets =
        candidateTickets?.tickets.filter(
          (t) => t.optionType === "GeneralCeremonyTicket"
        ) ?? [];
      return Math.min(availableVip, generalTickets.length);
    },
  },
  actions: {
    async getAllTickets() {
      const authStore = useAuthStore();
      const candidateId = authStore.user.candidateId;
      const { tickets }: { tickets: Array<CandidateTickets> } =
        await privateBoaHttp
          .get("tickets", { searchParams: { candidateId: candidateId || "" } })
          .json();
      this.candidateTickets = tickets;
      return tickets;
    },
    async saveTickets(
      tickets: Array<Partial<Ticket>>
    ): Promise<{ tickets: Array<Ticket>; success: boolean }> {
      const response: { tickets: Array<Ticket>; success: boolean } =
        await privateBoaHttp
          .post("tickets", {
            json: { tickets },
          })
          .json();
      for (const ticket of response.tickets) {
        const candidate = this.candidateTickets.find(
          (c) => c.candidatePK === ticket.GSI1_PK
        );
        if (candidate) {
          const index = candidate.tickets.findIndex((t) => t.SK === ticket.SK);
          if (index > -1) {
            candidate.tickets[index] = ticket;
          }
        }
      }
      return response;
    },
    async assignTickets(tickets: Array<Ticket>, idOrPK: string) {
      let candidateId = idOrPK;
      const match = idOrPK?.match(/CANDIDATE#(.*)/);
      if (match) {
        candidateId = match[1];
      }
      const response: { tickets: Array<CandidateTickets>; success: boolean } =
        await privateBoaHttp
          .post("tickets/assign", {
            json: { tickets, candidateId },
          })
          .json();
      this.candidateTickets = response.tickets;
    },
    async scanTicket(
      ticket: Ticket,
      options: { manual: boolean } = { manual: false }
    ): Promise<{
      ticket: Ticket & { alreadyScanned: boolean };
      sponsorship?: PurchasedProduct;
      drinkTickets: number;
      doorprizeSponsor: boolean;
    }> {
      const response: {
        success: boolean;
        ticket: Ticket & { alreadyScanned: boolean };
        sponsorship?: PurchasedProduct;
        drinkTickets: number;
        doorprizeSponsor: boolean;
      } = await privateBoaHttp
        .post("tickets/scan", {
          json: { ticket, options },
        })
        .json();
      if (response.ticket.scanned) {
        const candidate = this.candidateTickets.find((c) =>
          c.tickets.find((t) => t.SK === ticket.SK)
        );
        if (candidate) {
          const index = candidate?.tickets.findIndex((t) => t.SK === ticket.SK);
          candidate.tickets[index] = response.ticket;
        }
      }
      return response;
    },
    async sendTickets(
      tickets: Array<Ticket>,
      method: "all" | "email" | "sms" = "all",
      all: boolean = false
    ): Promise<
      | {
          success: true;
          sentTickets: Array<Ticket>;
          unassignedTickets: Array<Ticket>;
        }
      | { success: false; message: string }
    > {
      try {
        const response: {
          success: true;
          sentTickets: Array<Ticket>;
          unassignedTickets: Array<Ticket>;
        } = await privateBoaHttp
          .post("tickets/send", {
            json: {
              all,
              method,
              tickets,
            },
          })
          .json();
        return response;
      } catch (error) {
        if (error instanceof HTTPError) {
          const response: { message: string } = await error.response.json();
          return { success: false, message: response.message };
        }
        return { success: false, message: "Unknown error" };
      }
    },
    clear() {
      this.$reset();
    },
  },
});
